import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Link } from "@mui/material";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import BadgeRoundedIcon from "@mui/icons-material/BadgeRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import { pageTitle, lead, h2Rule, paragraph, fadeUp } from "../theme/marketingStyles";

/** /kwaliteit-en-erkenning — het feitelijke vettingverhaal: welke registers
 *  we raadplegen (FOD eCad, Psychologencommissie, beroepsverenigingen), hoe
 *  het verificatieproces loopt en wat her-verificatie inhoudt. Moet blijven
 *  kloppen met de werkelijke adminflow (nl.json admin.vettingGuide). */
export default function Quality() {
  const { t } = useTranslation();

  const steps = [
    { icon: <BadgeRoundedIcon />, key: "step1" },
    { icon: <FactCheckRoundedIcon />, key: "step2" },
    { icon: <ForumRoundedIcon />, key: "step3" },
    { icon: <AutorenewRoundedIcon />, key: "step4" },
  ];

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      <Container maxWidth="md" sx={{ pt: { xs: 6, sm: 9 }, pb: { xs: 7, sm: 10 } }}>
        <Box sx={{ textAlign: { xs: "center", md: "left" }, ...fadeUp(0) }}>
          <Typography variant="h1" sx={pageTitle}>{t("quality.title")}</Typography>
          <Typography sx={{ ...lead, maxWidth: 640, mx: { xs: "auto", md: 0 } }}>{t("quality.intro")}</Typography>
        </Box>

        {/* Verificatieproces als verticale rail — geen kaartendozen, dezelfde
            beeldtaal als de stappen op de homepage. */}
        <Box sx={fadeUp(120)}>
          {steps.map((s, i) => (
            <Box key={s.key} sx={{ display: "flex", gap: { xs: 2.5, sm: 3 }, alignItems: "stretch" }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box sx={{ width: 44, height: 44, borderRadius: "50%", flexShrink: 0, bgcolor: "var(--surface)", border: "1px solid var(--accent-300)", color: "var(--accent-700)", display: "grid", placeItems: "center", "& svg": { fontSize: 24 } }}>
                  {s.icon}
                </Box>
                {i < steps.length - 1 && <Box sx={{ width: "1px", flexGrow: 1, bgcolor: "var(--neutral-300)", my: 1 }} />}
              </Box>
              <Box sx={{ pb: i < steps.length - 1 ? 4 : 0, pt: 0.75 }}>
                <Typography variant="h3" sx={{ fontSize: "1.2rem", mb: 0.5 }}>{i + 1}. {t(`quality.${s.key}Title`)}</Typography>
                <Typography sx={{ m: 0, color: "text.secondary", lineHeight: 1.7 }}>{t(`quality.${s.key}Body`)}</Typography>
              </Box>
            </Box>
          ))}
        </Box>

        <Typography variant="h2" sx={h2Rule}>{t("quality.whoTitle")}</Typography>
        <Typography sx={paragraph}>{t("quality.who1")}</Typography>
        <Typography sx={paragraph}>{t("quality.who2")}</Typography>

        <Typography variant="h2" sx={h2Rule}>{t("quality.registersTitle")}</Typography>
        <Typography sx={paragraph}>{t("quality.registersIntro")}</Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 3, "& li::marker": { color: "var(--primary-300)" } }}>
          <Typography component="li" sx={{ mb: 1, color: "text.secondary", lineHeight: 1.7 }}>
            {t("quality.register1")}{" "}
            <Link href="https://www.health.belgium.be" target="_blank" rel="noreferrer">health.belgium.be</Link>
          </Typography>
          <Typography component="li" sx={{ mb: 1, color: "text.secondary", lineHeight: 1.7 }}>
            {t("quality.register2")}{" "}
            <Link href="https://www.compsy.be" target="_blank" rel="noreferrer">compsy.be</Link>
          </Typography>
          <Typography component="li" sx={{ mb: 1, color: "text.secondary", lineHeight: 1.7 }}>{t("quality.register3")}</Typography>
        </Box>

        <Typography variant="h2" sx={h2Rule}>{t("quality.honestTitle")}</Typography>
        <Typography sx={paragraph}>{t("quality.honest1")}</Typography>
        <Typography sx={paragraph}>
          {t("quality.honest2")}{" "}
          <Link component={RouterLink} to="/hoe-matchen-wij">{t("quality.matchLink")}</Link>
        </Typography>
      </Container>
    </Box>
  );
}
